import { Subject } from 'rxjs';
import isEqual from 'fast-deep-equal';
export var DATA_COLLECTION_HISTORY_TYPE;
(function (DATA_COLLECTION_HISTORY_TYPE) {
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Add"] = 0] = "Add";
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Remove"] = 1] = "Remove";
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Edit"] = 2] = "Edit";
})(DATA_COLLECTION_HISTORY_TYPE || (DATA_COLLECTION_HISTORY_TYPE = {}));
var defaultItemsComparator = function (one, two) { return isEqual(one, two); };
var DataCollection = /** @class */ (function () {
    function DataCollection(initial, idGetter, itemsComparator) {
        if (itemsComparator === void 0) { itemsComparator = defaultItemsComparator; }
        this.idGetter = idGetter;
        this.itemsComparator = itemsComparator;
        this.history = [];
        this.items = new Map(initial.map(function (item) { return [idGetter(item), item]; }));
        this.onCollectionChangedSubj = new Subject();
    }
    DataCollection.prototype.asObservable = function () {
        return this.onCollectionChangedSubj.asObservable();
    };
    DataCollection.prototype.reset = function (initial) {
        var _this = this;
        if (initial === void 0) { initial = []; }
        this.history = [];
        this.items = new Map(initial.map(function (item) { return [_this.idGetter(item), item]; }));
    };
    DataCollection.prototype.getItems = function () {
        return Array.from(this.items.values());
    };
    DataCollection.prototype.safeAddItem = function (added) {
        var id = this.idGetter(added);
        if (this.items.has(id))
            return new Error("item with id '" + id + "' has already been added");
        this.items.set(id, added);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Add, id: id, added: added });
    };
    DataCollection.prototype.addItem = function (added) {
        var result = this.safeAddItem(added);
        if (result !== undefined)
            throw result;
    };
    DataCollection.prototype.tryAddItem = function (added) {
        var result = this.safeAddItem(added);
        return result === undefined;
    };
    DataCollection.prototype.safeRemoveItem = function (id) {
        var removed = this.items.get(id);
        if (removed === undefined)
            return new Error("item with id '" + id + "' is not existed");
        this.items.delete(id);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Remove, id: id, removed: removed });
    };
    DataCollection.prototype.removeItem = function (id) {
        var result = this.safeRemoveItem(id);
        if (result !== undefined)
            throw result;
    };
    DataCollection.prototype.tryRemoveItem = function (id) {
        var result = this.safeRemoveItem(id);
        return result === undefined;
    };
    DataCollection.prototype.safeEditItem = function (id, newItem) {
        var newItemId = this.idGetter(newItem);
        if (newItemId !== id)
            return new Error("id('" + id + "') and newItem's id('" + newItemId + "') are not equal");
        var oldItem = this.items.get(id);
        if (oldItem === undefined)
            return new Error("item with id '" + id + "' is not existed");
        this.items.set(id, newItem);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Edit, id: id, oldItem: oldItem, newItem: newItem });
    };
    DataCollection.prototype.editItem = function (id, newItem) {
        var result = this.safeEditItem(id, newItem);
        if (result !== undefined)
            throw result;
    };
    DataCollection.prototype.tryEditItem = function (id, newItem) {
        var result = this.safeEditItem(id, newItem);
        return result === undefined;
    };
    DataCollection.prototype.addOrEditItem = function (newItem) {
        var id = this.idGetter(newItem);
        var oldItem = this.items.get(id);
        if (oldItem === undefined)
            this.addItem(newItem);
        else
            this.editItem(id, newItem);
    };
    DataCollection.prototype.getDiff = function () {
        var _this = this;
        var addedItems = new Map();
        var removedItems = new Map();
        var editedItems = new Map();
        this.history.forEach(function (record) {
            switch (record.type) {
                case DATA_COLLECTION_HISTORY_TYPE.Add:
                    {
                        // don't need check added. item cannot be added if key already exist
                        // don't need check edited. item cannot be added if key already exist
                        var removed = removedItems.get(record.id);
                        if (removed !== undefined) {
                            var areEqual = _this.itemsComparator(record.added, removed);
                            if (!areEqual) {
                                editedItems.set(record.id, { old: removed, new: record.added });
                            }
                            removedItems.delete(record.id);
                        }
                        else
                            addedItems.set(record.id, record.added);
                    }
                    break;
                case DATA_COLLECTION_HISTORY_TYPE.Remove:
                    {
                        // don't need check removed. item cannot be removed if key hasn't exist
                        var isAdded = addedItems.delete(record.id);
                        if (!isAdded) {
                            var edited = editedItems.get(record.id);
                            if (edited !== undefined) {
                                editedItems.delete(record.id);
                                removedItems.set(record.id, edited.old);
                            }
                            else
                                removedItems.set(record.id, record.removed);
                        }
                    }
                    break;
                case DATA_COLLECTION_HISTORY_TYPE.Edit:
                    {
                        // don't need check removed. item cannot be edited if key hasn't exist
                        var added = addedItems.get(record.id);
                        var edited = editedItems.get(record.id);
                        if (added !== undefined) {
                            addedItems.set(record.id, record.newItem);
                        }
                        else if (edited !== undefined) {
                            editedItems.set(record.id, { old: edited.old, new: record.newItem });
                        }
                        else
                            editedItems.set(record.id, {
                                old: record.oldItem,
                                new: record.newItem
                            });
                    }
                    break;
            }
        });
        return {
            added: Array.from(addedItems.values()),
            edited: Array.from(editedItems.values()),
            removed: Array.from(removedItems.values())
        };
    };
    DataCollection.prototype.isChanged = function () {
        var _a = this.getDiff(), added = _a.added, edited = _a.edited, removed = _a.removed;
        return added.length === 0 && edited.length === 0 && removed.length === 0;
    };
    DataCollection.prototype.applyHistoryRecord = function (record) {
        this.history.push(record);
        this.onCollectionChangedSubj.next(record);
    };
    return DataCollection;
}());
export { DataCollection };
//# sourceMappingURL=DataCollection.js.map